/**
 * =============================================================================
 * IDS CRM Web - The phone's copy of the spare parts list
 * =============================================================================
 *
 * Spare parts, Push 2 (build spec 6.6). Engineers search the list in Log Work,
 * often in a parlour with no signal, so the browser keeps a copy:
 *
 *  - localStorage key ids-crm-spare-parts-list-v1, value
 *    { version, fetchedAt, items: [{ id, number, name }] }. About 60-90 KB for
 *    the whole list, one copy per browser: no prices, the same for everyone.
 *    Every read and write is in try/catch; a full, private or blocked store
 *    keeps the copy in this module's memory only.
 *  - Not IndexedDB: bumping the Log Work database's version would block on
 *    tabs still open on the old one and drop unsent Log Work to memory.
 *  - GET /api/v1/parts/catalogue/picker?since=<version> at most every 5
 *    minutes, and when the phone comes back online. The version is a hash of
 *    the visible list, so "unchanged" only restamps fetchedAt.
 *  - One fetch is shared by every hook on the page.
 *
 * useSparePartsList returns the SAME items array until the version changes:
 * the picker's search depends on it (SearchSelect lists fetchOptions in its
 * search effect), so a new array each render would search on every render.
 * =============================================================================
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export const SPARE_PARTS_LIST_KEY = 'ids-crm-spare-parts-list-v1';
export const REFRESH_MIN_MS = 300000;
export const PICKER_URL = '/api/v1/parts/catalogue/picker';
// A fetch of the list not answered in 20 seconds is given up (weak signal).
export const FETCH_TIMEOUT_MS = 20000;

const EMPTY_ITEMS = Object.freeze([]);

// What localStorage held when this module last read or wrote it, so the same
// text is never parsed twice and every hook shares one items array.
let cache = { raw: undefined, copy: null };
// A copy that could not be written to localStorage (full, private, blocked).
let memoryOnly = null;
// The fetch in flight, shared by every hook.
let inflight = null;
// Did the last fetch fail?
let lastFailed = false;
// Has a fetch finished since the page loaded?
let settled = false;
const listeners = new Set();

const publish = () => listeners.forEach(listener => listener());

function tidyItems(items) {
  return (Array.isArray(items) ? items : [])
    .filter(item => item && item.id && typeof item.name === 'string')
    .map(item => ({
      id: String(item.id),
      number: item.number === null || item.number === undefined ? '' : String(item.number),
      name: item.name
    }));
}

function parseCopy(raw) {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    if (!value || typeof value.version !== 'string' || !Array.isArray(value.items)) return null;
    return {
      version: value.version,
      fetchedAt: Number(value.fetchedAt) || 0,
      items: tidyItems(value.items)
    };
  } catch (error) {
    return null;
  }
}

/** The copy this browser holds, or null. Parses localStorage only when it changed. */
export function readSparePartsCopy() {
  if (memoryOnly) return memoryOnly;
  let raw;
  try {
    raw = window.localStorage.getItem(SPARE_PARTS_LIST_KEY);
  } catch (error) {
    return cache.copy;
  }
  if (raw === cache.raw) return cache.copy;
  let copy = parseCopy(raw);
  // Another tab only restamped fetchedAt: keep the items array this tab
  // already hands out, so the picker does not search again for nothing.
  if (copy && cache.copy && cache.copy.version === copy.version && cache.copy.items.length === copy.items.length) {
    copy = { ...copy, items: cache.copy.items };
  }
  cache = { raw, copy };
  return cache.copy;
}

function storeCopy(copy) {
  const raw = JSON.stringify(copy);
  try {
    window.localStorage.setItem(SPARE_PARTS_LIST_KEY, raw);
    cache = { raw, copy };
    memoryOnly = null;
  } catch (error) {
    memoryOnly = copy;
  }
}

const isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

const isStale = (copy, now = Date.now()) => !copy || now - copy.fetchedAt > REFRESH_MIN_MS;

/**
 * Fetches the list (or the "unchanged" answer) once, however many hooks ask.
 * Never throws: a failure leaves the copy as it was.
 *
 * A fetch that has not answered in FETCH_TIMEOUT_MS is given up (and
 * aborted): on a weak signal a request can hang, and while it is in flight
 * every other hook, the online event and refresh() would only wait on it, so
 * a phone with no copy would say "Loading" for good. Given up, it counts as
 * failed and the next ask fetches again.
 */
export function fetchSparePartsList(authFetch) {
  if (inflight) return inflight;
  const run = (async () => {
    const held = readSparePartsCopy();
    const url = held && held.version ? `${PICKER_URL}?since=${encodeURIComponent(held.version)}` : PICKER_URL;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timer = null;
    const givenUp = new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        if (controller) controller.abort();
        reject(new Error('The parts list took too long to fetch'));
      }, FETCH_TIMEOUT_MS);
    });
    try {
      const response = await Promise.race([authFetch(url, controller ? { signal: controller.signal } : {}), givenUp]);
      if (!response || !response.ok) throw new Error(`The parts list could not be fetched (${response ? response.status : 'no answer'})`);
      const body = await Promise.race([response.json().catch(() => ({})), givenUp]);
      const now = Date.now();
      if (body && !body.unchanged && Array.isArray(body.items)) {
        storeCopy({ version: String(body.version ?? ''), fetchedAt: now, items: tidyItems(body.items) });
      } else if (held) {
        // "Unchanged", or a body with no items: keep the same items array.
        storeCopy({ ...held, fetchedAt: now });
      }
      lastFailed = false;
    } catch (error) {
      lastFailed = true;
    } finally {
      clearTimeout(timer);
    }
  })();
  inflight = run.finally(() => {
    inflight = null;
    settled = true;
    publish();
  });
  publish();
  return inflight;
}

/** For tests only: forget the module's copy, fetch and listeners' state. */
export function resetSparePartsListForTests() {
  cache = { raw: undefined, copy: null };
  memoryOnly = null;
  inflight = null;
  lastFailed = false;
  settled = false;
}

/**
 * The spare parts list for the picker.
 *
 * @param {{ authFetch: Function, enabled?: boolean }} options
 * @returns {{ items: Array<{id, number, name}>, status: 'ready'|'loading'|'none'|'error', version: string|null, refresh: () => Promise<void> }}
 *   status: 'ready' a copy is held; 'loading' the first fetch, no copy yet;
 *   'none' no copy and no signal; 'error' no copy and the fetch failed.
 */
export function useSparePartsList({ authFetch, enabled = true } = {}) {
  const [copy, setCopy] = useState(() => readSparePartsCopy());
  const [fetching, setFetching] = useState(() => Boolean(inflight));
  const [failed, setFailed] = useState(() => lastFailed);
  const [done, setDone] = useState(() => settled);

  useEffect(() => {
    const sync = () => {
      setCopy(readSparePartsCopy());
      setFetching(Boolean(inflight));
      setFailed(lastFailed);
      setDone(settled);
    };
    listeners.add(sync);
    // Another hook may have fetched between this one's first render and now.
    sync();
    return () => { listeners.delete(sync); };
  }, []);

  // Held in a ref: a page that hands in a new authFetch each render must not
  // start a fetch each render.
  const fetchRef = useRef(authFetch);
  fetchRef.current = authFetch;
  const canFetch = Boolean(enabled) && typeof authFetch === 'function';

  useEffect(() => {
    if (!canFetch) return undefined;
    const maybeFetch = () => {
      if (isOnline() && isStale(readSparePartsCopy())) fetchSparePartsList(fetchRef.current);
    };
    maybeFetch();
    window.addEventListener('online', maybeFetch);
    return () => window.removeEventListener('online', maybeFetch);
  }, [canFetch]);

  const refresh = useCallback(() => {
    if (!canFetch) return Promise.resolve();
    return fetchSparePartsList(fetchRef.current);
  }, [canFetch]);

  let status;
  if (copy) status = 'ready';
  else if (fetching) status = 'loading';
  else if (!isOnline()) status = 'none';
  else if (canFetch && !done) status = 'loading';
  else if (failed) status = 'error';
  else status = 'none';

  return {
    items: copy ? copy.items : EMPTY_ITEMS,
    status,
    version: copy ? copy.version : null,
    refresh
  };
}

/**
 * Searches the list: every word typed must appear in the name or the Item
 * Number. Ranked exact Item Number first, then Item Numbers that start with
 * the term, then names that start with it, then the rest; ties by name.
 *
 * @param {Array<{id, number, name}>} items
 * @param {string} term
 * @param {number} [limit=30]
 * @returns {Array<{id, number, name}>}
 */
export function searchSpareParts(items, term, limit = 30) {
  const whole = String(term ?? '').trim().toLowerCase();
  const words = whole.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const ranked = [];
  for (const item of Array.isArray(items) ? items : []) {
    const name = String(item.name || '').toLowerCase();
    const number = String(item.number || '').toLowerCase();
    const haystack = `${name} ${number}`;
    if (!words.every(word => haystack.includes(word))) continue;
    let rank = 3;
    if (number && number === whole) rank = 0;
    else if (number && number.startsWith(whole)) rank = 1;
    else if (name.startsWith(whole)) rank = 2;
    ranked.push({ item, rank, name, number });
  }
  ranked.sort((a, b) => (a.rank - b.rank)
    || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
    || (a.number < b.number ? -1 : a.number > b.number ? 1 : 0));
  return ranked.slice(0, limit).map(entry => entry.item);
}
